import React, { useState, useEffect, useCallback } from 'react';
import {
  FaClipboardList, FaPaperPlane, FaCheck, FaTimes, FaEye, FaPlus, FaMapMarkerAlt,
} from 'react-icons/fa';
import api, { errorMessage, getRole } from '../api';
import { MANAGEMENT, hasRole } from './attendance/roles';
import {
  PageShell, Card, Badge, Button, Modal, Field, inputClass, Alert, Loading,
  Table, EmptyState, formatDateTime, MONTHS,
} from './attendance/ui';

/**
 * Rural attendance reporting: supervisors at sites without biometric
 * connectivity submit a monthly sheet, management reviews it centrally.
 */
const RuralSubmissions = () => {
  const canReview = hasRole(getRole(), MANAGEMENT);

  const now = new Date();
  const previousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  const [submissions, setSubmissions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [composing, setComposing] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reviewComment, setReviewComment] = useState('');

  const [draft, setDraft] = useState({
    month: previousMonth.getMonth() + 1,
    year: previousMonth.getFullYear(),
    location: '',
    notes: '',
    totalWorkingDays: 22,
    rows: [],
  });

  const load = useCallback(async () => {
    setError('');
    try {
      const [submissionRes, locationRes] = await Promise.all([
        api.get('monthly-submissions/'),
        api.get('locations/', { params: { type: 'rural' } }),
      ]);
      setSubmissions(submissionRes.data);
      setLocations(locationRes.data);
    } catch (err) {
      setError(errorMessage(err, 'Could not load rural submissions.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /** Pull the staff at a location into an editable sheet. */
  const startSheet = async (locationId) => {
    setDraft((current) => ({ ...current, location: locationId, rows: [] }));
    if (!locationId) return;
    try {
      const { data } = await api.get('employees/', { params: { location: locationId } });
      setDraft((current) => ({
        ...current,
        rows: data.filter((employee) => employee.status === 'active').map((employee) => ({
          employeeId: employee.employeeId,
          employeeName: employee.name,
          daysPresent: 0, daysAbsent: 0, lateDays: 0, leaveDays: 0,
          totalHours: 0, overtimeHours: 0, remarks: '',
        })),
      }));
    } catch (err) {
      setError(errorMessage(err, 'Could not load staff for this location.'));
    }
  };

  const setCell = (index, key, value) =>
    setDraft((current) => ({
      ...current,
      rows: current.rows.map((row, position) =>
        position === index ? { ...row, [key]: value } : row),
    }));

  const submitSheet = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('monthly-submissions/', {
        month: Number(draft.month),
        year: Number(draft.year),
        location: draft.location,
        notes: draft.notes,
        totalWorkingDays: Number(draft.totalWorkingDays),
        data: draft.rows.map((row) => ({
          ...row,
          daysPresent: Number(row.daysPresent) || 0,
          daysAbsent: Number(row.daysAbsent) || 0,
          lateDays: Number(row.lateDays) || 0,
          leaveDays: Number(row.leaveDays) || 0,
          totalHours: Number(row.totalHours) || 0,
          overtimeHours: Number(row.overtimeHours) || 0,
        })),
      });
      setComposing(false);
      setNotice('Monthly report submitted. Management has been notified.');
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not submit the report.'));
    } finally {
      setSaving(false);
    }
  };

  const review = async (submission, decision) => {
    setSaving(true);
    setError('');
    try {
      await api.post(`monthly-submissions/${submission.id}/${decision}/`,
        { comment: reviewComment });
      setNotice(`Report ${decision === 'approve' ? 'approved' : 'rejected'}.`);
      setViewing(null);
      setReviewComment('');
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not record the decision.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <PageShell title="Rural Attendance Reports"><Loading /></PageShell>;

  return (
    <PageShell
      title="Rural Attendance Reports"
      subtitle="Monthly sheets from sites without biometric connectivity"
      actions={
        <Button onClick={() => { setComposing(true); setDraft((current) => ({ ...current, rows: [] })); }}>
          <FaPlus /> New monthly report
        </Button>
      }
    >
      <Alert>{error}</Alert>
      <Alert tone="success">{notice}</Alert>

      <Card title={`Submissions (${submissions.length})`}>
        {submissions.length === 0 ? (
          <EmptyState icon={<FaClipboardList />} title="No monthly reports yet"
            hint="Supervisors at rural sites submit their staff attendance here each month." />
        ) : (
          <Table
            columns={['Period', 'Location', 'Staff', 'Working days', 'Submitted by',
              'Submitted', 'Status', 'Reviewer', '']}
            rows={submissions}
            renderRow={(submission) => (
              <tr key={submission.id} className="border-b last:border-0 hover:bg-gray-50">
                <td className="py-3 pr-4 font-medium text-gray-800 whitespace-nowrap">
                  {submission.monthName} {submission.year}
                </td>
                <td className="py-3 pr-4 whitespace-nowrap">
                  <span className="flex items-center gap-1.5">
                    <FaMapMarkerAlt className="text-gray-400 text-xs" /> {submission.location}
                  </span>
                </td>
                <td className="py-3 pr-4">{submission.data?.length || 0}</td>
                <td className="py-3 pr-4">{submission.totalWorkingDays}</td>
                <td className="py-3 pr-4 whitespace-nowrap">{submission.submittedBy || '—'}</td>
                <td className="py-3 pr-4 whitespace-nowrap text-xs">{formatDateTime(submission.submittedAt)}</td>
                <td className="py-3 pr-4"><Badge value={submission.status} /></td>
                <td className="py-3 pr-4 text-gray-500">
                  {submission.reviewedBy || '—'}
                  {submission.reviewComment && (
                    <div className="text-xs text-gray-400 italic">“{submission.reviewComment}”</div>
                  )}
                </td>
                <td className="py-3 pr-4">
                  <button onClick={() => { setViewing(submission); setReviewComment(''); }}
                    className="p-2 text-gray-400 hover:text-[#15A857] hover:bg-green-50 rounded-lg" title="View">
                    <FaEye />
                  </button>
                </td>
              </tr>
            )}
          />
        )}
      </Card>

      {/* Compose */}
      <Modal isOpen={composing} onClose={() => setComposing(false)}
        title="Submit monthly attendance report" size="max-w-5xl">
        <form onSubmit={submitSheet} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <Field label="Location *">
              <select required value={draft.location} className={inputClass}
                onChange={(event) => startSheet(event.target.value)}>
                <option value="">Select a rural site…</option>
                {locations.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
            </Field>
            <Field label="Month">
              <select value={draft.month} className={inputClass}
                onChange={(event) => setDraft({ ...draft, month: event.target.value })}>
                {MONTHS.map((name, index) => <option key={name} value={index + 1}>{name}</option>)}
              </select>
            </Field>
            <Field label="Year">
              <select value={draft.year} className={inputClass}
                onChange={(event) => setDraft({ ...draft, year: event.target.value })}>
                {Array.from({ length: 4 }, (_, index) => now.getFullYear() - index)
                  .map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </Field>
            <Field label="Total working days">
              <input type="number" min="0" max="31" value={draft.totalWorkingDays} className={inputClass}
                onChange={(event) => setDraft({ ...draft, totalWorkingDays: event.target.value })} />
            </Field>
          </div>

          {draft.rows.length === 0 ? (
            <Alert tone="info">Choose a rural location to load its staff list.</Alert>
          ) : (
            <div className="overflow-x-auto border border-gray-100 rounded-xl">
              <table className="w-full min-w-[860px] text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-left text-gray-500">
                    {['Staff', 'Present', 'Absent', 'Late', 'Leave', 'Total hours', 'Overtime', 'Remarks']
                      .map((column) => (
                        <th key={column} className="py-2.5 px-3 font-semibold whitespace-nowrap">{column}</th>
                      ))}
                  </tr>
                </thead>
                <tbody>
                  {draft.rows.map((row, index) => (
                    <tr key={row.employeeId} className="border-t">
                      <td className="py-2 px-3">
                        <div className="font-medium text-gray-800 whitespace-nowrap">{row.employeeName}</div>
                        <div className="text-xs text-gray-500 font-mono">{row.employeeId}</div>
                      </td>
                      {['daysPresent', 'daysAbsent', 'lateDays', 'leaveDays', 'totalHours', 'overtimeHours']
                        .map((key) => (
                          <td key={key} className="py-2 px-3">
                            <input type="number" min="0" step={key.includes('Hours') ? '0.5' : '1'}
                              value={row[key]} onChange={(event) => setCell(index, key, event.target.value)}
                              className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#15A857]" />
                          </td>
                        ))}
                      <td className="py-2 px-3">
                        <input value={row.remarks} onChange={(event) => setCell(index, 'remarks', event.target.value)}
                          className="w-full min-w-[120px] px-2 py-1.5 border border-gray-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-[#15A857]" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <Field label="Notes for management">
            <textarea rows="3" value={draft.notes} className={inputClass}
              placeholder="Anything management should know about this month."
              onChange={(event) => setDraft({ ...draft, notes: event.target.value })} />
          </Field>

          <div className="flex gap-3">
            <Button type="submit" disabled={saving || draft.rows.length === 0} className="flex-1">
              <FaPaperPlane /> {saving ? 'Submitting…' : 'Submit report'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setComposing(false)}>Cancel</Button>
          </div>
        </form>
      </Modal>

      {/* View / review */}
      <Modal isOpen={!!viewing} onClose={() => setViewing(null)}
        title={viewing ? `${viewing.location} · ${viewing.monthName} ${viewing.year}` : ''} size="max-w-5xl">
        {viewing && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div><span className="text-gray-500 block">Submitted by</span><strong>{viewing.submittedBy || '—'}</strong></div>
              <div><span className="text-gray-500 block">Submitted</span><strong>{formatDateTime(viewing.submittedAt)}</strong></div>
              <div><span className="text-gray-500 block">Working days</span><strong>{viewing.totalWorkingDays}</strong></div>
              <div><span className="text-gray-500 block">Status</span><Badge value={viewing.status} /></div>
            </div>

            {viewing.notes && (
              <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-700">{viewing.notes}</div>
            )}

            <Table
              columns={['Staff ID', 'Name', 'Present', 'Absent', 'Late', 'Leave', 'Hours', 'Overtime', 'Remarks']}
              rows={viewing.data || []}
              empty="This report contains no staff rows."
              renderRow={(row) => (
                <tr key={row.employeeId} className="border-b last:border-0">
                  <td className="py-2.5 pr-4 font-mono text-xs">{row.employeeId}</td>
                  <td className="py-2.5 pr-4 font-medium text-gray-800 whitespace-nowrap">{row.employeeName}</td>
                  <td className="py-2.5 pr-4 text-green-700 font-semibold">{row.daysPresent}</td>
                  <td className="py-2.5 pr-4 text-red-600">{row.daysAbsent}</td>
                  <td className="py-2.5 pr-4 text-amber-600">{row.lateDays}</td>
                  <td className="py-2.5 pr-4 text-purple-600">{row.leaveDays}</td>
                  <td className="py-2.5 pr-4">{row.totalHours}</td>
                  <td className="py-2.5 pr-4">{row.overtimeHours}</td>
                  <td className="py-2.5 pr-4 text-gray-500">{row.remarks || '—'}</td>
                </tr>
              )}
            />

            {canReview && viewing.status === 'submitted' && (
              <div className="border-t pt-4 space-y-3">
                <Field label="Review comment">
                  <textarea rows="2" value={reviewComment} className={inputClass}
                    onChange={(event) => setReviewComment(event.target.value)}
                    placeholder="Optional note sent back to the supervisor." />
                </Field>
                <div className="flex gap-3">
                  <Button onClick={() => review(viewing, 'approve')} disabled={saving} className="flex-1">
                    <FaCheck /> Approve report
                  </Button>
                  <Button variant="danger" onClick={() => review(viewing, 'reject')} disabled={saving}>
                    <FaTimes /> Reject
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </PageShell>
  );
};

export default RuralSubmissions;
